import { HotelSearchRequestType } from "src/Modules/Hotels/constants/SearchPage";
import BaseApi from "src/store/apis/BaseApi";

const PrivateAPIWithTags = BaseApi.enhanceEndpoints({
  addTagTypes: [""],
});

type ResponseType<T> = {
  message: string;
  errorCode: number;
  referenceId: string;
  result: T;
};

export type HotelResponseType = {
  indexUid: "hotels" | "cities";
  id: string;
  code: string;
  name: string;
  cityCode: string;
  cityName: string;
  countryCode: string;
  countryName: string;
};

export type CancelPolicy = {
  fromDate: string | null;
  chargeType: string | null;
  cancellationCharge: number;
  originalCurrency?: string | null;
  convertedCancellationCharge?: number;
  convertedCurrency?: string | null;
};

export type Supplement = {
  index: number;
  type: string | null;
  description: string | null;
  price: number;
  currency: string | null;
};

export type HotelRoom = {
  bookingCode: string | null;
  roomTypeName: string[] | null;
  extraGuestCharges: number | null;
  inclusion: string | null;
  mealType: string | null;
  isRefundable: boolean;
  totalFare: number;
  totalOriginalFare: number | null;
  totalTax: number;
  totalOriginalTax: number | null;
  roomPromotion: string[];
  cancelPolicies: CancelPolicy[];
  supplements: Supplement[];
};

export type Hotel = {
  hotelCode: string;
  hotelName: string;
  category: string;
  address: string | null;
  cityCode: string | null;
  cityName: string | null;
  countryCode: string | null;
  countryName: string | null;
  description: string | null;
  image: string | null;
  images: string[];
  attractions: string[];
  facilities: string[];
  categorizedFacilities: { category: string; items: string[] }[];
  hotelWebsiteUrl: string | null;
  email: string | null;
  phoneNumber: string | null;
  faxNumber: string | null;
  pinCode: string | null;
  longitude: number | null;
  latitude: number | null;
  hotelRating: number | null;
  checkInTime: string | null;
  checkOutTime: string | null;
  minPrice: number | null;
  currency: string | null;
  originalCurrency: string | null;
  rooms: HotelRoom[];
};

export type SearchSessionType = {
  id: string;
  startedAt: string;
  expiresAt: string;
};

export type SearchRoomsResponseType = {
  hotels: Hotel[];
  totalCount: number;
  offset: number;
  isLastPolling: boolean;
  pointsFactor: number;
  session: SearchSessionType;
};

export type StoredHotelBookingData = {
  hotel: Hotel;
  pointsFactor: number;
  sessionId: string;
  _session?: { startedAt: number; maxTime: number };
};

export const HOTEL_BOOKING_PAYMENT_MODES = {
  LIMIT: "Limit",
  SAVED_CARD: "SavedCard",
  NEW_CARD: "NewCard",
} as const;

export const HOTEL_BOOKING_CUSTOMER_TITLES = {
  MR: "Mr",
  MRS: "Mrs",
  MS: "Ms",
} as const;

export const HOTEL_BOOKING_GUEST_TYPES = {
  ADULT: "Adult",
  CHILD: "Child",
} as const;

export type HotelBookingPaymentMode = (typeof HOTEL_BOOKING_PAYMENT_MODES)[keyof typeof HOTEL_BOOKING_PAYMENT_MODES];
export type HotelBookingCustomerTitle = (typeof HOTEL_BOOKING_CUSTOMER_TITLES)[keyof typeof HOTEL_BOOKING_CUSTOMER_TITLES];
export type HotelBookingGuestType = (typeof HOTEL_BOOKING_GUEST_TYPES)[keyof typeof HOTEL_BOOKING_GUEST_TYPES];

// Hotel Booking Request Types
export type CustomerName = {
  title: HotelBookingCustomerTitle;
  firstName: string;
  lastName: string;
  type: HotelBookingGuestType;
};

export type CustomerDetail = {
  customerNames: CustomerName[];
};

export type CardHolderAddress = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  countryCode: string;
};

export type PaymentInfo = {
  cvvNumber: string;
  cardNumber: string;
  cardExpirationMonth: string;
  cardExpirationYear: string;
  cardHolderFirstName: string;
  cardHolderLastName: string;
  billingAmount: number;
  billingCurrency: string;
  cardHolderAddress: CardHolderAddress;
};

export type PaymentState = {
  pointsToPay: number;
  cashToPay: number;
  selection: "PayAll" | "PayCustom";
};

// ─── Initiate Request ──────────────────────────────────────────────────────────

export type HotelInitiateRequest = {
  sessionId: string;
  bookingCode: string;
  cityCode: string;
  hotelCode: string;
  hotelName: string;
  cityName: string;
  roomCount: number;
  amount: number;
  totalPrice: number;
  paymentMode: HotelBookingPaymentMode;
  searchTotalFare: number;
  customerDetails: CustomerDetail[];
  clientReferenceId: string;
  bookingReferenceId: string;
  emailId: string;
  phoneNumber: string;
  checkInDate: string | null;
  checkIn: string;
  checkOut: string;
  returnUrl: string;
  paymentInfo?: PaymentInfo;
};

export type HotelInitiateResponse = {
  referenceNumber: string;
};

// ─── Book Request ──────────────────────────────────────────────────────────────

export type HotelBookRequest = {
  referenceNumber: string;
  paymentInfo?: PaymentInfo;
  TempPaymentState?: PaymentState;
};

export type HotelBookResponse = {
  confirmationNumber: string;
  referenceNumber: string;
};

// ─── Booking Detail ────────────────────────────────────────────────────────────

export const OrderStatus = { Pending: 0, Confirmed: 1, Failed: 2 } as const;
export type OrderStatusType = (typeof OrderStatus)[keyof typeof OrderStatus];

export const PaymentStatus = { Pending: 0, Confirmed: 1, Failed: 2 } as const;
export type PaymentStatusType = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const BookingStatus = { Pending: 0, Confirmed: 1, Failed: 2 } as const;
export type BookingStatusType = (typeof BookingStatus)[keyof typeof BookingStatus];

export type BookingDetailRequest = {
  referenceNumber: string;
};

export type BookingDetailHotelDetails = {
  hotelCode: string | null;
  hotelName: string | null;
  rating: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  map: string | null;
  city: string | null;
  image: string | null;
  images: string[];
};

export type BookingCancelPolicy = {
  index?: number | null;
  fromDate: string | null;
  chargeType: string | null;
  cancellationCharge: number;
  originalCurrency?: string | null;
  convertedCancellationCharge?: number;
  convertedCurrency?: string | null;
};

export type BookingRoomSupplement = {
  index: number;
  type: string | null;
  description: string | null;
  price: number;
  currency: string | null;
};

export type BookingDetailRoom = {
  name: string[];
  inclusion: string | null;
  convertedPrice: number;
  originalPrice: number;
  originalTax: number;
  convertedTax: number;
  roomPromotion: string | null;
  mealType: string | null;
  isRefundable: boolean;
  cancelPolicies: BookingCancelPolicy[];
  supplements: BookingRoomSupplement[][];
  customerDetails: CustomerDetail[];
};

export type RateConditionRoom = {
  name: string[];
  inclusion: string | null;
  roomPromotion: string[];
  cancelPolicies: CancelPolicy[];
  isRefundable: boolean;
  supplements: Supplement[][];
  amenities: string[];
};

export type RateConditionsResult = {
  hotelCode: string;
  currency: string;
  rateConditions: string[];
  rooms: RateConditionRoom[];
};

export type BookingDetailResult = {
  orderStatus: number;
  paymentStatus: PaymentStatusType;
  bookingStatus: BookingStatusType;
  voucherStatus: boolean;
  referenceNumber: string;
  confirmationNumber: string | null;
  bookingReferenceId: string;
  hotelConfirmationNumber: string | null;
  invoiceNumber: string | null;
  checkIn: string;
  checkOut: string;
  bookingDate: string;
  noOfRooms: number;
  currency: string;
  amount: number;
  totalFare: number;
  totalTax: number;
  pointsUsed: number;
  hotelDetails: BookingDetailHotelDetails;
  rooms: BookingDetailRoom[];
  rateConditions: string[];
};

// ─── Legacy types (used by booking result page localStorage data) ─────────────

export type CreditCardBillingOption = {
  cardType: string | null;
  charge: number;
  chargeType: string | null;
  currency: string | null;
};

export type BookingRoom = {
  name: string[];
  bookingCode: string;
  inclusion: string | null;
  convertedPrice: number;
  originalPrice: number;
  originalTax: number;
  totalTax: number;
  extraGuestCharges: string | null;
  roomPromotion: string[];
  cancelPolicies: BookingCancelPolicy[];
  mealType: string | null;
  isRefundable: boolean;
  withTransfers: boolean;
  amenities: string[];
  supplements: BookingRoomSupplement[][];
};

export type HotelBookingResponseData = {
  hotelCode: string;
  currency: string;
  priceChanged: boolean;
  rooms: BookingRoom[];
  rateConditions: string[];
  creditCardBillingOptions: CreditCardBillingOption[];
  confirmationNumber: string | null;
  clientReferenceId: string | null;
  bookingReferenceId: string | null;
  message: string | null;
};

export const HotelsApi = PrivateAPIWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getHotelsAndCites: builder.query<{ results: HotelResponseType[]; totalCount: number }, { query: string }>({
      query: ({ query }) => {
        return {
          url: `hotels/search/destinations`,
          method: "GET",
          params: {
            query,
          },
        };
      },
      transformResponse: (response: ResponseType<{ results: HotelResponseType[]; totalCount: number }>) => {
        if (response?.result?.results?.length === 0) throw new Error("No Hotels or Cities");
        return response.result;
      },
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    searchRooms: builder.query<SearchRoomsResponseType, { request: HotelSearchRequestType; cacheBuster?: string }>({
      query: ({ request }) => ({
        url: `hotels/search/rooms`,
        method: "POST",
        body: { ...request},
      }),
      transformResponse: (response: ResponseType<SearchRoomsResponseType>) => {
        return response.result;
      },
      keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    initiateHotelBooking: builder.mutation<HotelInitiateResponse, HotelInitiateRequest>({
      query: (request) => ({
        url: `hotels/initiate`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: ResponseType<HotelInitiateResponse>) => response.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getBookingDetail: builder.query<BookingDetailResult, BookingDetailRequest>({
      query: (request) => ({
        url: `hotels/orderDetail`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: ResponseType<BookingDetailResult>) => response.result,
      extraOptions: {
        notifyOnFailure: true,
      },
      keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
    }),
    getRateConditions: builder.query<RateConditionsResult, {bookingCode:string}>({
      query: (request) => ({
        url: `hotels/rateconditions`,
        method: "POST",
        body: request,
      }),
      transformResponse: (response: ResponseType<RateConditionsResult>) => response.result,
      extraOptions: {
        notifyOnFailure: true,
      },
      keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
    }),

  }),
});

type HotelsApiEndpoints = keyof typeof HotelsApi.endpoints;
type HotelsHookReturnType = ReturnType<typeof HotelsApi.useGetHotelsAndCitesQuery>;
type SearchRoomsHookReturnType = ReturnType<typeof HotelsApi.useSearchRoomsQuery>;
export { type HotelsApiEndpoints, type HotelsHookReturnType, type SearchRoomsHookReturnType };
export const {
  useLazyGetHotelsAndCitesQuery,
  useSearchRoomsQuery,
  useLazySearchRoomsQuery,
  useInitiateHotelBookingMutation,
  useGetBookingDetailQuery,
  useLazyGetBookingDetailQuery,
  useGetRateConditionsQuery,
  useLazyGetRateConditionsQuery
} = HotelsApi;
